import { useState, type FC } from 'react';
import { Copy, Check, BarChart3 } from 'lucide-react';

interface ProfileResult {
  detectedPersona?: string;
  painPoints?: string[];
  relevantFeatures?: string[];
  approachMessage?: string;
  toneRecommendation?: string;
  confidenceScore?: number;
  rawContent?: string;
}

const CopyBtn: FC<{ text: string; label?: string }> = ({ text, label = 'Copier' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copié !' : label}
    </button>
  );
};

const ConfidenceBar: FC<{ score: number }> = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 75 ? 'bg-emerald-500 dark:bg-emerald-400' : pct >= 50 ? 'bg-amber-500 dark:bg-amber-400' : 'bg-red-500 dark:bg-red-400';
  const label = pct >= 75 ? 'text-emerald-700 dark:text-emerald-300' : pct >= 50 ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300';

  return (
    <div className="flex items-center gap-3">
      <BarChart3 className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Score de confiance</span>
          <span className={`text-sm font-bold ${label}`}>{pct}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

interface ProspectDataDisplayProps {
  result: ProfileResult;
}

const ProspectDataDisplay: FC<ProspectDataDisplayProps> = ({ result }) => (
  <div className="fc-card p-5 space-y-5">
    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Analyse du prospect</h3>

    {/* Confidence Score */}
    {typeof result.confidenceScore === 'number' && <ConfidenceBar score={result.confidenceScore * 100} />}

    {/* Detected Persona + Tone */}
    <div className="flex flex-wrap gap-2">
      {result.detectedPersona && (
        <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-300">
          Persona : {result.detectedPersona}
        </span>
      )}
      {result.toneRecommendation && (
        <span className="inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/30 px-3 py-1 text-xs font-medium text-purple-700 dark:text-purple-300">
          Ton : {result.toneRecommendation}
        </span>
      )}
    </div>

    {/* Pain Points */}
    {result.painPoints && result.painPoints.length > 0 && (
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Points de douleur</p>
        <ul className="space-y-1.5">
          {result.painPoints.map((pain, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />
              {pain}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Relevant Features */}
    {result.relevantFeatures && result.relevantFeatures.length > 0 && (
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Features pertinentes</p>
        <ul className="space-y-1.5">
          {result.relevantFeatures.map((feat, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
              {feat}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Approach Message */}
    {result.approachMessage && (
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Message d&apos;approche</p>
          <CopyBtn text={result.approachMessage} label="Copier message" />
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
          <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed">{result.approachMessage}</p>
        </div>
      </div>
    )}

    {/* Fallback: Raw Content when structured fields are incomplete */}
    {result.rawContent && (!result.painPoints?.length || !result.approachMessage) && (
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Analyse complète</p>
          <CopyBtn text={result.rawContent} label="Copier l&apos;analyse" />
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4 max-h-96 overflow-y-auto">
          <div className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed prose prose-sm dark:prose-invert max-w-none">
            {result.rawContent}
          </div>
        </div>
      </div>
    )}
  </div>
);

export default ProspectDataDisplay;
